import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import meterTagAPI from '../services/meterTagAPI'
import { MeterTagEntity } from '../types/meterTag.types'

const STALE_TIME_MS = 60_000

export const useMeterTagsBulk = (productNumbers: string[]) => {
  const key = [...productNumbers].sort().join(',')
  const query = useQuery({
    queryKey: ['meter-tags-bulk', key],
    queryFn: () => meterTagAPI.getMeterTagsBulk(productNumbers),
    enabled: productNumbers.length > 0,
    staleTime: STALE_TIME_MS,
  })

  const byProductNumber = useMemo(() => {
    const map: Record<string, string[]> = {}
    for (const entry of query.data ?? []) {
      map[entry.product_number] = entry.tags ?? []
    }
    return map
  }, [query.data])

  return {
    byProductNumber,
    isLoading: query.isLoading,
  }
}

export const useMeterTagFilter = (tags: string[]) => {
  const key = [...tags].sort().join(',')
  const query = useQuery({
    queryKey: ['meter-tags-filter', key],
    queryFn: () => meterTagAPI.getMeterTagsByTags(tags),
    enabled: tags.length > 0,
    staleTime: STALE_TIME_MS,
  })

  const matchingProductNumbers = useMemo(
    () => (query.data ?? []).map((entry: MeterTagEntity) => entry.product_number),
    [query.data],
  )

  return {
    matchingProductNumbers,
    isActive: tags.length > 0,
    isLoading: query.isLoading,
  }
}

export const useKnownMeterTags = () => {
  const query = useQuery({
    queryKey: ['meter-tags-known'],
    queryFn: () => meterTagAPI.getAllMeterTags(),
    staleTime: STALE_TIME_MS,
  })
  const tags = useMemo(() => {
    const set = new Set<string>()
    for (const entry of query.data ?? []) {
      for (const t of entry.tags ?? []) set.add(t)
    }
    return Array.from(set).sort()
  }, [query.data])
  return tags
}

export const useMeterTagSingle = (productNumber?: string) => {
  return useQuery({
    queryKey: ['meter-tag-single', productNumber ?? ''],
    queryFn: () => meterTagAPI.getMeterTag(productNumber ?? ''),
    enabled: Boolean(productNumber),
    staleTime: STALE_TIME_MS,
  })
}

const applyTagsToBulkCache = (
  queryClient: ReturnType<typeof useQueryClient>,
  productNumber: string,
  tags: string[],
) => {
  queryClient.setQueriesData<MeterTagEntity[] | undefined>(
    ['meter-tags-bulk'],
    (old) => {
      if (!old) return [{ product_number: productNumber, tags }]
      const existing = old.find((e) => e.product_number === productNumber)
      if (existing) {
        return old.map((e) =>
          e.product_number === productNumber ? { ...e, tags } : e,
        )
      }
      return [...old, { product_number: productNumber, tags }]
    },
  )
}

export const useUpdateMeterTag = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ productNumber, tags }: { productNumber: string; tags: string[] }) => {
      if (tags.length === 0) {
        await meterTagAPI.deleteMeterTag(productNumber).catch(() => undefined)
        return { product_number: productNumber, tags: [] } as MeterTagEntity
      }
      return meterTagAPI.upsertMeterTag(productNumber, tags)
    },
    onSuccess: (result, { productNumber, tags }) => {
      const effectiveTags = result?.tags ?? tags
      queryClient.setQueryData<MeterTagEntity | null>(
        ['meter-tag-single', productNumber],
        effectiveTags.length === 0
          ? null
          : { product_number: productNumber, tags: effectiveTags },
      )
      applyTagsToBulkCache(queryClient, productNumber, effectiveTags)
      queryClient.invalidateQueries(['meter-tags-bulk'])
      queryClient.invalidateQueries(['meter-tags-filter'])
      queryClient.invalidateQueries(['meter-tags-known'])
    },
  })
}
